import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarCheck, CalendarPlus, Pencil, Send, Tags, Trash2 } from "lucide-react";

import { eventService, type EventCategory, type EventFeedback, type EventTask, type SchoolEvent } from "../../services/eventService";
import { todayIso } from "../../utils/dates";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { EventCategoriesModal, EventForm, RegisterForm } from "./EventForms";
import { FeedbackSummary, GuestList, Planning } from "./EventParts";
import { EVENT_TYPES, canWithdraw, dateRange, eventConfig, eventStatus, labelFrom, places, registrationBlock, timeRange } from "./eventsLogic";

/** The school's events: planning them, publishing them, and who is coming. */
export function EventsPanel({ events, categories, tasks, feedback, isLoading, isError, onRetry, canManage, onChanged, initialView }: {
  events?: SchoolEvent[];
  categories: EventCategory[];
  tasks: EventTask[];
  feedback: EventFeedback[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
  initialView?: string;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<SchoolEvent | "new" | null>(null);
  const [registering, setRegistering] = useState<SchoolEvent | null>(null);
  const [managingCategories, setManagingCategories] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const publish = useMutation({ mutationFn: eventService.publishEvent, onSuccess: (row) => done(`${row.name} is published${row.is_public ? " for parents and learners to see" : " for staff"}.`) });
  const complete = useMutation({ mutationFn: eventService.completeEvent, onSuccess: (row) => done(`${row.name}: the register is closed. ${row.attended_count} came.`) });
  const postpone = useMutation({ mutationFn: eventService.postponeEvent, onSuccess: (row) => done(`${row.name} is postponed. Change its dates, then publish it again.`) });
  const cancel = useMutation({ mutationFn: eventService.cancelEvent, onSuccess: (row) => done(`${row.name} was cancelled.`, true) });
  const remove = useMutation({ mutationFn: eventService.deleteEvent, onSuccess: () => done("The draft was deleted.", true) });
  const signIn = useMutation({ mutationFn: (row: SchoolEvent) => eventService.updateEvent({ id: row.id, data: { show_on_sign_in: !row.show_on_sign_in } }), onSuccess: () => onChanged() });
  const withdraw = useMutation({ mutationFn: eventService.withdraw, onSuccess: () => done("You have withdrawn. You can register again while registration is open.") });
  const actions = [publish, complete, postpone, cancel, remove, signIn, withdraw];

  const columns: RegisterColumn<SchoolEvent>[] = [
    { header: "Date", cell: (row) => dateRange(row), exportValue: (row) => dateRange(row), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Time", cell: (row) => timeRange(row) || "—", exportValue: (row) => timeRange(row), className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Event",
      cell: (row) => (
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: row.category_color }} aria-hidden />
          <span className="font-medium text-gray-900">{row.name}</span>
          {canManage && !row.is_public && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">Staff only</span>}
        </span>
      ),
      exportValue: (row) => row.name,
      className: "min-w-[14rem]",
    },
    { header: "Category", cell: (row) => row.category_name, exportValue: (row) => row.category_name, className: "text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(EVENT_TYPES, row.event_type), exportValue: (row) => labelFrom(EVENT_TYPES, row.event_type), className: "text-gray-700" },
    { header: "Venue", cell: (row) => row.venue || "—", exportValue: (row) => row.venue ?? "", className: "text-gray-700" },
    { header: "Registered", cell: (row) => (row.registration_required ? places(row) : "—"), exportValue: (row) => (row.registration_required ? places(row) : ""), align: "right", className: "tabular-nums text-gray-700" },
    ...(canManage ? [{ header: "Came", cell: (row: SchoolEvent) => (row.status === "completed" || row.attended_count ? row.attended_count : "—"), exportValue: (row: SchoolEvent) => row.attended_count, align: "right" as const, className: "tabular-nums text-gray-700" }] : []),
    { header: "Status", cell: (row) => <StatusDot {...eventStatus(row, today)} />, exportValue: (row) => eventStatus(row, today).label },
    { header: "Organiser", cell: (row) => row.organizer_name, exportValue: (row) => row.organizer_name, exportOnly: true },
  ];

  const event = viewing !== null ? events?.find((row) => row.id === viewing) : undefined;
  const block = event ? registrationBlock(event, today) : null;
  const closed = event ? event.status === "cancelled" || event.status === "completed" : false;
  const rating = event?.average_rating != null ? `${event.average_rating.toFixed(1)} of 5 from ${event.feedback_count}` : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Events"
        description={canManage ? "Open an event to publish it, see who is coming, check people in and plan it." : "Open an event to see the details and register."}
        actions={canManage && (
          <>
            <button type="button" onClick={() => setManagingCategories(true)} className="btn-outline h-8 min-h-8 px-3 text-xs">
              <Tags className="mr-1.5 h-3.5 w-3.5" /> Categories
            </button>
            <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
              <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> New event
            </button>
          </>
        )}
        rows={events}
        config={eventConfig(today, canManage)}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          actions.forEach((mutation) => mutation.reset());
          setViewing(row.id);
        }}
        noun={["event", "events"]}
        exportName={{ file: "school-events", title: "School Events" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No events yet. Add categories, then the first event." : "No events to show."}
      />

      {event && (
        <DetailsModal
          size="4xl"
          title={event.name}
          headline={{ label: `${event.category_name} · ${labelFrom(EVENT_TYPES, event.event_type)}`, value: dateRange(event), note: [timeRange(event), event.venue].filter(Boolean).join(" · ") || "Time and venue to be announced" }}
          status={eventStatus(event, today)}
          notice={
            canManage && event.status === "published" && event.end_date < today ? (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">This event is over. Close the register to mark everyone not checked in as absent and open feedback.</p>
            ) : event.status === "draft" ? (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">A draft: only staff can see it until it is published.</p>
            ) : undefined
          }
          facts={[
            ["Organised by", event.organizer_name],
            ["Contact", [event.contact_person, event.contact_phone, event.contact_email].filter(Boolean).join(", ") || undefined],
            ["Registration", event.registration_required ? `${places(event)} registered${event.registration_deadline ? `, closes ${event.registration_deadline}` : ""}` : "Not needed"],
            ...(canManage
              ? ([
                  ["Came", event.status === "completed" || event.attended_count ? String(event.attended_count) : undefined],
                  ["Expected turnout", event.expected_attendees ? String(event.expected_attendees) : undefined],
                  ["Who sees it", `${event.is_public ? "Parents, learners and staff" : "Staff only"}${event.show_on_sign_in && event.is_public ? "; on the sign-in page" : ""}`],
                ] as [string, string | undefined][])
              : []),
            ["Rating", rating],
            ["Address", event.address ?? undefined],
          ]}
          sections={[
            { title: event.status === "postponed" ? "Why it was postponed" : "Why it was cancelled", body: event.status === "postponed" || event.status === "cancelled" ? `${event.status_reason}${event.status_changed_by_name ? `\n— ${event.status_changed_by_name}` : ""}` : "", tone: event.status === "cancelled" ? "danger" : "warning" },
            { title: "About", body: event.description },
            { title: "Programme", body: event.agenda },
            { title: "What to bring or wear", body: event.requirements },
            ...(canManage ? [{ title: "Staff notes", body: event.notes }] : []),
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canManage && event.status === "draft" && !event.registered_count && (
                <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(event.id)} className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> Delete the draft
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && !closed && (
                <button type="button" onClick={() => setEditing(event)} className="btn btn-outline gap-1.5">
                  <Pencil className="h-4 w-4" /> Change
                </button>
              )}
              {canManage && (event.status === "draft" || event.status === "postponed") && (
                <button type="button" disabled={publish.isPending || event.end_date < today} title={event.end_date < today ? "Change its dates first: it is already over." : undefined} onClick={() => publish.mutate(event.id)} className="btn btn-primary gap-1.5">
                  <Send className="h-4 w-4" /> Publish
                </button>
              )}
              {canManage && event.status === "published" && event.start_date <= today && (
                <button type="button" disabled={complete.isPending} onClick={() => complete.mutate(event.id)} className="btn btn-primary gap-1.5" title="Everyone not checked in is marked as not having come.">
                  <CalendarCheck className="h-4 w-4" /> Close the register
                </button>
              )}
              {!canManage && !event.my_registration && !block && (
                <button type="button" onClick={() => setRegistering(event)} className="btn btn-primary gap-1.5">
                  <CalendarCheck className="h-4 w-4" /> Register
                </button>
              )}
            </>
          }
        >
          {actions.map((mutation, index) => mutation.isError && mutation !== postpone && mutation !== cancel && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}

          <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {event.my_registration_status === "attended" ? "You came to this event." : event.my_registration_status === "no_show" ? "You were registered but not checked in." : event.my_registration ? "You are registered." : block ?? "Registration is open."}
              </p>
              {event.my_registration && event.my_registration_status !== "attended" && event.my_registration_status !== "no_show" && <p className="text-xs text-gray-500">You can withdraw until the day it starts.</p>}
            </div>
            <div className="flex gap-2">
              {event.my_registration && canWithdraw({ status: event.my_registration_status ?? "registered", event_start_date: event.start_date, has_feedback: event.my_feedback }, today) && (
                <button type="button" disabled={withdraw.isPending} onClick={() => withdraw.mutate(event.my_registration!)} className="btn btn-outline">Withdraw</button>
              )}
              {canManage && !event.my_registration && !block && (
                <button type="button" onClick={() => setRegistering(event)} className="btn btn-outline">Register myself</button>
              )}
            </div>
          </section>

          {canManage && event.is_public && !closed && (
            <label className="flex items-start gap-2 rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700">
              <input type="checkbox" checked={event.show_on_sign_in} disabled={signIn.isPending} onChange={() => signIn.mutate(event)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
              <span>
                <span className="font-medium">Show it on the sign-in page</span>
                <span className="block text-xs text-gray-500">Anyone can see the sign-in page without an account.{event.show_on_sign_in && event.status !== "published" ? " It shows once published." : ""}</span>
              </span>
            </label>
          )}

          {canManage && (event.registration_required || event.registered_count > 0) && <GuestList event={event} today={today} onChanged={onChanged} />}
          {canManage && <FeedbackSummary feedback={feedback.filter((row) => row.event === event.id)} />}
          {canManage && <Planning event={event} events={events ?? []} tasks={tasks} today={today} onChanged={onChanged} />}

          {canManage && event.status === "published" && event.start_date > today && (
            <ReasonPanel
              title="Postpone it"
              description="Registrations are kept. Change the dates, then publish it again."
              placeholder="e.g. The hall is being repaired; a new date will follow."
              context={{ key: "events.note", params: { kind: "postpone", id: event.id } }}
              button="Postpone the event"
              busy={postpone.isPending}
              error={postpone.error}
              errorText="The event could not be postponed."
              onSubmit={(reason) => postpone.mutate({ id: event.id, reason })}
            />
          )}
          {canManage && ["draft", "published", "postponed"].includes(event.status) && (
            <ReasonPanel
              title="Cancel the event"
              description={event.registered_count ? `${event.registered_count === 1 ? "The 1 person" : `The ${event.registered_count} people`} registered will see it is cancelled, and why.` : "It stays on the calendar, marked cancelled."}
              placeholder="e.g. Called off because of the national exams timetable."
              context={{ key: "events.note", params: { kind: "cancel", id: event.id } }}
              button="Cancel the event"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The event could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: event.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {editing && (
        <EventForm
          event={editing === "new" ? undefined : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            setEditing(null);
            done(editing === "new" ? `${saved.name} saved as a draft. Publish it when it is ready.` : `${saved.name} updated.`);
            setViewing(saved.id);
          }}
        />
      )}
      {registering && (
        <RegisterForm
          event={registering}
          onClose={() => setRegistering(null)}
          onDone={() => {
            setRegistering(null);
            done(`You are registered for ${registering.name}.`);
          }}
        />
      )}
      {managingCategories && <EventCategoriesModal categories={categories} onClose={() => setManagingCategories(false)} onChanged={onChanged} />}
    </div>
  );
}
